import { translations } from './translations';

export const DEFAULT_LANGUAGE = 'en';
export const LANGUAGE_STORAGE_KEY = 'jobjourney.language';
export const SUPPORTED_LANGUAGES = ['en', 'zh-CN'] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export type TranslationValues = Record<string, string | number>;
type RuntimeTranslationCatalog = Record<Language, unknown>;

export function isSupportedLanguage(value: string): value is Language {
  return SUPPORTED_LANGUAGES.includes(value as Language);
}

export function normalizeLanguage(value: string | undefined | null): Language {
  if (!value) {
    return DEFAULT_LANGUAGE;
  }

  if (isSupportedLanguage(value)) {
    return value;
  }

  const normalized = value.toLowerCase();

  if (normalized.startsWith('zh')) {
    return 'zh-CN';
  }

  if (normalized.startsWith('en')) {
    return 'en';
  }

  return DEFAULT_LANGUAGE;
}

export function getStoredLanguage(storage: Pick<Storage, 'getItem'> = window.localStorage): Language {
  try {
    return normalizeLanguage(storage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function setStoredLanguage(
  language: Language,
  storage: Pick<Storage, 'setItem'> = window.localStorage
) {
  try {
    storage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Ignore storage errors so private browsing modes still work.
  }
}

export function getInitialLanguage(): Language {
  const storedLanguage = getStoredLanguage();

  if (storedLanguage !== DEFAULT_LANGUAGE) {
    return storedLanguage;
  }

  return normalizeLanguage(window.navigator.language);
}

export function translate(
  key: string,
  language: Language,
  values?: TranslationValues,
  catalog: RuntimeTranslationCatalog = translations
) {
  const translatedValue =
    getTranslationValue(catalog[language], key) ??
    getTranslationValue(catalog[DEFAULT_LANGUAGE], key) ??
    key;

  if (!values || typeof translatedValue !== 'string') {
    return String(translatedValue);
  }

  return Object.entries(values).reduce(
    (message, [name, value]) => message.replaceAll(`{{${name}}}`, String(value)),
    translatedValue
  );
}

function getTranslationValue(tree: unknown, key: string): unknown {
  return key.split('.').reduce<unknown>((currentValue, pathPart) => {
    if (!currentValue || typeof currentValue !== 'object') {
      return undefined;
    }

    return (currentValue as Record<string, unknown>)[pathPart];
  }, tree);
}
